// 多路同步的本地來源（SPEC PLAY-03「多路同步的本地來源」第 2 點，app/vms#25）：每一路的本地錄影主機（NVR / Kinio Station 的通道走主機、
// 獨立攝影機走自己的 SD）。雲端沒有、也沒有開放的本地錄影主機的才帶鎖頭。
import type { VmsDevice } from '../../lib/devices';
import { useQueries } from '@tanstack/react-query';
import { localRecordingDays } from '@kinio/web-sdk';
import { localUnsupported } from '../../lib/localCoverage';
import { serialByHost } from '../../lib/hostQueue';
import { CLOUD_PROBE_MAX, monthsBack, type CloudState } from './cloudCams';
import { sourcePlan, type SourcePlan } from './view';

/** 多路同步開放的本地種類：硬碟（NVR / Station）與 SD 卡（手機當攝影機的不算：它的本地錄影在手機上）。 */
export const LOCAL_KINDS: readonly string[] = ['nvr', 'sd'];

/** 這一路的本地來源（主機 / 通道 / 種類）；沒有開放的本地種類 → null。 */
export function localPlanOf(cam: VmsDevice, tree: VmsDevice[]): SourcePlan | null {
  const parent = cam.parentAid ? tree.find((d) => d.aid === cam.parentAid && d.homeId === cam.homeId) : undefined;
  const p = sourcePlan(cam, parent);
  return LOCAL_KINDS.includes(p.localKind) ? p : null;
}

/** 錄影主機的鍵（同單路 hostKey 的前半）：每台主機同時最多 LOCAL_PER_HOST 路。 */
export function hostOf(cam: VmsDevice, plan: SourcePlan): string {
  return `${cam.homeId}/${plan.localAid}`;
}

/** 這一路接得上錄影主機（主機或自己在線）。 */
export function localOnline(cam: VmsDevice, tree: VmsDevice[]): boolean {
  const parent = cam.parentAid ? tree.find((d) => d.aid === cam.parentAid && d.homeId === cam.homeId) : undefined;
  return (parent ?? cam).presence === 'online';
}

/**
 * 選攝影機時的本地狀態：NVR / Kinio Station 的通道 → unknown（可能有，不帶鎖頭；載入時照 pb.timeline 逐路判斷），**不問 pb.days**——
 * 2026-10-22 dev 實測 Station 一次 pb.days 要 17 秒（超過 RPC 等候 15 秒），還佔住設備處理雲端 RPC 的那條緒，同時問就逾時、
 * 設備短暫被判離線。獨立攝影機（SD 卡）照舊問 pb.days（這個月有就不問上個月；同一台一次一個），沒有 SD 的帶鎖頭。
 * 沒有開放的本地種類 → no。
 */
export function useLocalCams(cameras: VmsDevice[], tree: VmsDevice[]): { stateOf: (key: string) => CloudState; loading: boolean } {
  const lanes = cameras
    .map((c) => ({ c, plan: localPlanOf(c, tree) }))
    .filter((x): x is { c: VmsDevice; plan: SourcePlan } => !!x.plan);
  const sd = lanes.filter((x) => x.plan.localKind === 'sd').slice(0, CLOUD_PROBE_MAX);
  const months = monthsBack(new Date(), 2);
  const qs = useQueries({
    queries: sd.map(({ c, plan }) => ({
      queryKey: ['local-days-recent', c.homeId, plan.localAid, months.join()],
      staleTime: 10 * 60_000,
      retry: false,
      enabled: localOnline(c, tree),
      queryFn: async () => {
        try {
          for (const m of months) {
            const d = await serialByHost(`${c.apiEndpoint}|${c.homeId}/${plan.localAid}`, () => localRecordingDays(c.apiEndpoint, c.homeId, plan.localAid, m));
            if (Array.isArray(d) && d.length > 0) return true;
          }
          return false;
        } catch (e) {
          if (localUnsupported(e)) return false; // 沒有 SD（不支援）= 沒有；其他（逾時、離線）= 不知道
          throw e;
        }
      },
    })),
  });
  const m = new Map<string, CloudState>();
  for (const { c } of lanes) m.set(c.key, 'unknown');
  sd.forEach(({ c }, i) => {
    const q = qs[i];
    m.set(c.key, q.isSuccess ? (q.data ? 'yes' : 'no') : 'unknown');
  });
  return { stateOf: (k) => m.get(k) ?? (cameras.some((c) => c.key === k && !localPlanOf(c, tree)) ? 'no' : 'unknown'), loading: qs.some((q) => q.isLoading) };
}
